import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Dialog, DialogTitle, IconButton, Box, Button, Alert } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BackdropProgressBar from "../../components/ui/BackdropProgressBar";
import {
  OTPFOR,
  OTPMODE,
  OTPSENTMODE,
  USERINFO,
  OTPSTATUS,
  GLOBALSETTINGS,
  SYSTEMSETTINGS,
  VISIBLE_DIGITS,
} from "../../data/constants";
import { USERSIGNUPMESSAGES, USERSIGNINMESSAGES } from "../../constants/messages";
import useApi from "../../hooks/useApi";
import { HrEmployeeOtpDto, OtpResponseDto } from "../../types/User";
import { createAndSendOtp, verifyOtp } from "../../services/userService";
import { useSettingValue } from "../../services/useSettingValue";
import CommonResendOtpStatus from "../signin/CommonResendOtpStatus";
import OtpCountDown from "../CommonComponent/OtpCountDown";
import { useUser } from "../../context/UserContext";
import { obfuscateEmail, obfuscatePhone } from "../../utils/maskUtils";
import OtpInput from "../CommonComponent/CustomOTP";

interface OtpVerificationComponentProps {
  email: string;
  phone: string;
  userName: string;
  otpSendTo: string;
  onOtpVerified: (otp: string, isVerified: boolean) => void;
  onClose: () => void;
  open: boolean;
  onIsSessionLocked: () => void;
}
export interface OtpVerificationComponentRef {
  childComponetCall: () => Promise<boolean>;
}
const OtpVerificationComponent = forwardRef<
  OtpVerificationComponentRef,
  OtpVerificationComponentProps
>(({ email, phone, userName, otpSendTo, onOtpVerified, onClose, open, onIsSessionLocked }, ref) => {
  const [otpInput, setOtpInput] = useState("");
  const [otpSentMode, setOtpSentMode] = useState("");
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [isSessionLocked, setIsSessionLocked] = React.useState(false);
  const [otpStatus, setOtpStatus] = useState({
    isOtpSent: false,
    otpSentTo: "",
    isVisibleSendOtp: true,
    otpErrorMessage: "",
    timer: 0,
    isResendDisabled: true,
    isVerified: false,
  });
  const { userId } = useUser();
  OtpVerificationComponent.displayName = "";
  const otpRef = useRef(null);
  const { loading, executeApi } = useApi();
  const updateOtpStatusState = useCallback((newState: Partial<typeof otpStatus>) => {
    setOtpStatus((prevState) => ({
      ...prevState,
      ...newState,
    }));
  }, []);
  useImperativeHandle(ref, () => ({
    childComponetCall: async () => {
      return onSendOtp();
    },
  }));
  const otpFieldLength =
    Number(useSettingValue(SYSTEMSETTINGS.OTP_FIELD_LENGTH)) ||
    GLOBALSETTINGS.DEFAULT_OTP_FIELD_LENGTH;
  useEffect(() => {
    let countdown: ReturnType<typeof setTimeout>;
    if (otpStatus.timer > 0) {
      countdown = setTimeout(() => updateOtpStatusState({ timer: otpStatus.timer - 1 }), 1000);
    } else {
      updateOtpStatusState({ isResendDisabled: false });
    }
    return () => clearTimeout(countdown);
  }, [otpStatus.timer, updateOtpStatusState]);
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeLeft]);
  const createOtpData = (): HrEmployeeOtpDto => ({
    emailAddress: email,
    phone: phone,
    userName: userName,
    otpSendTo: otpSendTo,
    otpFor: OTPFOR.SIGNUP,
    sessionValue: localStorage.getItem(USERINFO.SESSIONVALUE),
    mode: OTPMODE.SEND,
    userId: userId,
  });
  const onSendOtp = async () => {
    clearOtp();
    const otpData: HrEmployeeOtpDto = createOtpData();
    const response = await executeApi<HrEmployeeOtpDto, OtpResponseDto>(createAndSendOtp, otpData);
    if (response) {
      updateOtpStatusState({ isOtpSent: true, otpSentTo: response.otpSentTo });
      setOtpSentMode(response.otpSentTo === OTPMODE.EMAIL ? OTPSENTMODE.EMAIL : OTPSENTMODE.PHONE);
      startTimer();
      setTimeLeft(response.verificationTimeOut);
      setTimeout(() => {
        focusFirstTextField();
      }, 100);

      return true;
    }
    return false;
  };

  const startTimer = () => {
    updateOtpStatusState({ timer: 30, isResendDisabled: true });
  };

  const handleResendOTP = async () => {
    const otpData: HrEmployeeOtpDto = createOtpData();
    const response = await executeApi<HrEmployeeOtpDto, OtpResponseDto>(createAndSendOtp, otpData);
    if (response) {
      startTimer();
      clearOtp();
      setTimeLeft(response.verificationTimeOut);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpInput) {
      updateOtpStatusState({
        otpErrorMessage: USERSIGNUPMESSAGES.OTP_CODE_REQUIRED,
      });
      focusFirstTextField();
    } else if (otpInput.length < otpFieldLength) {
      updateOtpStatusState({
        otpErrorMessage: USERSIGNUPMESSAGES.OTP_CODE_6_DIGIT_NUMBER + " " + otpSentMode,
      });
      focusFirstTextField();
    } else {
      const empAndOtpData: HrEmployeeOtpDto = {
        sessionValue: localStorage.getItem(USERINFO.SESSIONVALUE),
        verificationCode: otpInput,
        mode: OTPMODE.VERIFY,
      };

      const response = await executeApi<HrEmployeeOtpDto, OtpResponseDto>(verifyOtp, empAndOtpData);
      if (response.result === OTPSTATUS.MATCHED) {
        updateOtpStatusState({ isVerified: true, isOtpSent: false });
        onOtpVerified(otpInput, true);
        onClose();
      } else if (response.result === OTPSTATUS.EXPIRED) {
        updateOtpStatusState({
          otpErrorMessage: USERSIGNUPMESSAGES.OTP_CODE_EXPIRED,
          isVerified: false,
        });
      } else if (response.result === OTPSTATUS.SESSION_LOCKED) {
        setIsSessionLocked(true);
        updateOtpStatusState({
          isVerified: false,
        });
        onIsSessionLocked();
      } else {
        updateOtpStatusState({
          otpErrorMessage: USERSIGNUPMESSAGES.OTP_CODE_DOES_NOT_MATCH,
          isVerified: false,
        });
      }
    }
  };

  const clearOtp = () => {
    setOtpInput("");
    otpRef.current?.clear();
    focusFirstTextField();
    updateOtpStatusState({ otpErrorMessage: "" });
  };

  const focusFirstTextField = () => {
    if (!otpRef.current?.isComplete()) {
      otpRef.current?.focusFirstEmpty();
      return;
    }
  };

  const handleCloseDialog = () => {
    onClose();
  };

  const handleCustomOtpChange = (otp) => {
    setOtpInput(otp);
  };
  return (
    <Dialog
      onClose={handleCloseDialog}
      aria-labelledby="customized-dialog-title"
      open={open}
      maxWidth="xs"
      PaperProps={{ sx: { width: 480, height: 420 } }}
    >
      <DialogTitle
        id="customized-dialog-title"
        sx={{ fontWeight: "bold", textAlign: "center" }}
        className="dialogtitle-font-size"
      >
        OTP Verification for {otpSentMode}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleCloseDialog}
        sx={{
          position: "absolute",
          right: 12,
          top: 12,
          color: "#fff",
        }}
      >
        <CloseIcon />
      </IconButton>
      {/* {otpStatus.isOtpSent && ( */}
      <>
        <BackdropProgressBar loading={loading} />
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <img
            src={otpStatus.otpSentTo === OTPMODE.EMAIL ? "/otp-email.gif" : "/otp-mobile.gif"}
            alt="description"
            style={{ width: "100px", height: "100px" }}
          />
        </div>
        {isSessionLocked && (
          <Alert severity="error" sx={{ marginBottom: "10px" }} className="alert-alert">
            {USERSIGNINMESSAGES.SESSION_LOCKED_MESSAGE.replace(
              "{Session_value}",
              localStorage.getItem(USERINFO.SESSIONVALUE),
            )}
          </Alert>
        )}
        <div className={isSessionLocked ? "disabled-div" : ""}>
          <Alert severity="error" icon={false} className="alert-alert masked" sx={{ mx: 2 }}>
            We have sent you <strong>One Time Password</strong> to your{" "}
            {otpSentMode === OTPSENTMODE.EMAIL
              ? `${otpSentMode} (${obfuscateEmail(email)})`
              : `${otpSentMode} (${obfuscatePhone(phone, VISIBLE_DIGITS)})`}
          </Alert>
          <OtpCountDown timeLeft={timeLeft} />
          <div className="div-align">
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                px: 2,
              }}
            >
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                gap={2}
                sx={{
                  flexWrap: "wrap",
                  width: "100%",
                  maxWidth: {
                    xs: "100%",
                    sm: `${otpFieldLength * 63}px`,
                  },
                }}
              >
                <OtpInput length={otpFieldLength} onChange={handleCustomOtpChange} ref={otpRef} />
              </Box>
            </Box>
            <div>
              <CommonResendOtpStatus
                otpErrorMessage={otpStatus.otpErrorMessage}
                timer={otpStatus.timer}
                isResendDisabled={otpStatus.isResendDisabled}
                handleResendOTP={handleResendOTP}
              />
            </div>
            <Button type="button" variant="contained" onClick={handleVerifyOtp}>
              Verify
            </Button>
          </div>
        </div>
      </>
      {/* )} */}
    </Dialog>
  );
});

export default OtpVerificationComponent;
